import { useState, useEffect, useRef } from "react";
import { CRISIS_RESOURCES } from "../lib/crisis";

/**
 * App header — deliberately quiet.
 *
 * It carries identity and a way out, and nothing else — the date belongs to the
 * page, not to the chrome. The hairline underneath only appears once something
 * has scrolled beneath it, so opening the app shows no chrome at all.
 *
 * The mark is two concentric circles: a still centre inside an open ring. It
 * used to be a mountain summit, which is climb-and-peak iconography — exactly
 * the framing this product is trying to get out from under.
 *
 * The popup is a plain popup rather than a role="menu" (which would promise
 * APG roving-focus behaviour we do not implement). Focus moves into it on open
 * and back to the avatar on close.
 *
 * It carries support as well as the way out. In a product that holds mood
 * journals and runs crisis detection, having the lifelines reachable *only*
 * when a detector fires is backwards: it means help is something the app
 * decides you need. Here it is a permanent, unremarkable entry you can open on
 * a calm day, folded away until asked for so the popup never greets anyone
 * with hotline numbers they didn't reach for.
 */
export default function Header({ username, onSignOut }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const accountRef = useRef(null);
  const avatarRef = useRef(null);
  const firstItemRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Presentation only: fade the header hairline in once content passes under it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the account menu on outside tap or Escape.
  useEffect(() => {
    if (!showMenu) return;
    const onPointerDown = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setShowMenu(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowMenu(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showMenu]);

  // Move focus into the popup on open, and hand it back to the trigger on close
  // so a keyboard or switch user is never left standing outside it.
  useEffect(() => {
    if (showMenu) {
      wasOpenRef.current = true;
      firstItemRef.current?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      setShowSupport(false);
      avatarRef.current?.focus();
    }
  }, [showMenu]);

  const initial = (username || "").trim().charAt(0) || "•";

  return (
    <header className="shell-header" data-scrolled={scrolled ? "true" : "false"}>
      <span className="shell-brand">
        <span className="shell-brand-mark" aria-hidden="true">
          <svg width="19" height="19" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8.4" stroke="currentColor" strokeWidth="1.3" opacity="0.34" />
            <circle cx="10" cy="10" r="3.7" fill="currentColor" />
          </svg>
        </span>
        <span className="shell-brand-word">elevate</span>
      </span>

      <div className="shell-header-right">
        {username && (
          <div className="shell-account" ref={accountRef}>
            <button
              type="button"
              ref={avatarRef}
              className="shell-avatar"
              aria-label={`Account, signed in as ${username}`}
              aria-expanded={showMenu}
              onClick={() => setShowMenu((p) => !p)}
            >
              <span className="shell-avatar-disc" aria-hidden="true">{initial}</span>
            </button>

            {showMenu && (
              <div className="shell-menu">
                <p className="shell-menu-who">Signed in as {username}</p>

                <button
                  type="button"
                  ref={firstItemRef}
                  className="shell-menu-item"
                  aria-expanded={showSupport}
                  onClick={() => setShowSupport((p) => !p)}
                >
                  <span>Support</span>
                  <span className="shell-menu-chev" data-open={showSupport ? "true" : "false"} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 5 16 12 9 19" />
                    </svg>
                  </span>
                </button>

                {showSupport && (
                  <div className="shell-menu-support">
                    {CRISIS_RESOURCES.lines.map((l) => (
                      <a key={l.label} href={l.href} className="shell-menu-item shell-menu-item--line">
                        <span className="shell-menu-line-label">{l.label}</span>
                        <span className="shell-menu-line-detail">{l.detail}</span>
                      </a>
                    ))}
                    <p className="shell-menu-note">Free, confidential, any time. You don&apos;t need a reason.</p>
                  </div>
                )}

                <button
                  type="button"
                  className="shell-menu-item"
                  onClick={() => {
                    setShowMenu(false);
                    onSignOut();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
